'use client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useSpecialtyCalculators } from '@/lib/hooks/useSpecialtyCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  getCalculatorOutputAccordionStyles,
  OutputAlert,
  OutputField,
  Styled2ColGrid,
  StyledColumn,
  StyledTextSlider,
} from './components';

const MILESTONE_LABELS = ['Foundation / Slab', 'Frame', 'Lock-up', 'Fix-out', 'Completion'];

type ConstructionLoanFormValues = {
  landCost: number;
  buildCost: number;
  contingencyPercent: number;
  deposit: number;
  annualRate: number;
  constructionMonths: number;
  totalTermYears: number;
  milestone0: number;
  milestone1: number;
  milestone2: number;
  milestone3: number;
  milestone4: number;
};

const ConstructionLoan = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateConstructionLoan } = useSpecialtyCalculators();

  const methods = useForm<ConstructionLoanFormValues>({
    defaultValues: {
      landCost: 300000,
      buildCost: 400000,
      contingencyPercent: 10,
      deposit: 100000,
      annualRate: 6.5,
      constructionMonths: 12,
      totalTermYears: 30,
      milestone0: 10,
      milestone1: 15,
      milestone2: 35,
      milestone3: 25,
      milestone4: 15,
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const milestonePercents: [number, number, number, number, number] = [
    values.milestone0 || 0,
    values.milestone1 || 0,
    values.milestone2 || 0,
    values.milestone3 || 0,
    values.milestone4 || 0,
  ];
  const milestoneSum = milestonePercents.reduce((a, b) => a + b, 0);
  const milestoneSumOk = Math.abs(milestoneSum - 100) < 0.01;

  const result = calculateConstructionLoan({
    landCost: values.landCost || 0,
    buildCost: values.buildCost || 0,
    contingencyPercent: values.contingencyPercent || 0,
    deposit: values.deposit || 0,
    annualRate: values.annualRate || 0,
    constructionMonths: values.constructionMonths || 0,
    totalTermYears: values.totalTermYears || 0,
    milestonePercents,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="ConstructionLoan"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Enter construction details'}>
          <Controller
            name="landCost"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Land Cost"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="buildCost"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Build Cost"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="contingencyPercent"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Contingency"
                suffix="%"
                value={field.value}
                maxValue={30}
                defaultMax={30}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="deposit"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Deposit"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="annualRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Annual Interest Rate"
                suffix="%"
                value={field.value}
                defaultMax={20}
                step={0.1}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="constructionMonths"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Construction Period"
                suffix=" months"
                value={field.value}
                minValue={1}
                maxValue={36}
                defaultMax={36}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="totalTermYears"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Total Loan Term"
                suffix={field.value === 1 ? ' year' : ' years'}
                value={field.value}
                minValue={1}
                maxValue={35}
                onChange={field.onChange}
              />
            )}
          />

          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: 'text.primary', mb: 1, textTransform: 'uppercase' }}
            >
              Construction Milestones
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              Sum: {milestoneSum}% {!milestoneSumOk && '⚠ must equal 100%'}
            </Typography>
            {(['milestone0', 'milestone1', 'milestone2', 'milestone3', 'milestone4'] as const).map(
              (name, i) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  render={({ field }) => (
                    <StyledTextSlider
                      label={MILESTONE_LABELS[i]}
                      suffix="%"
                      value={field.value}
                      maxValue={100}
                      onChange={field.onChange}
                    />
                  )}
                />
              )
            )}
          </Box>
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Construction Loan Summary'}>
          {!milestoneSumOk && (
            <OutputAlert>
              Milestone percentages must sum to 100% (currently {milestoneSum}%).
            </OutputAlert>
          )}
          {result.lvrWarning && (
            <OutputAlert>
              LVR of {result.lvr}% exceeds the typical 80% maximum for construction loans.
            </OutputAlert>
          )}
          <Styled2ColGrid>
            <OutputField label="Total Project Cost" value={`$${result.totalProjectCost}`} />
            <OutputField label="Total Loan Required" value={`$${result.totalLoanRequired}`} />
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField label={`LVR${result.lvrWarning ? ' ⚠' : ''}`} value={`${result.lvr}%`} />
            <OutputField label="Interest During Construction" value={`$${result.ioInterest}`} />
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField label="Monthly P&I Payment" value={`$${result.piRepayment}`} />
            <OutputField label="Total P&I Repayments" value={`$${result.totalPIRepayments}`} />
          </Styled2ColGrid>
          <OutputField
            label="Total Financing Cost"
            value={`$${result.totalFinancingCost}`}
            highlight
          />
          <StyledColumn>
            <Accordion sx={getCalculatorOutputAccordionStyles(theme)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 30 }} />}>
                <Typography>Milestone Drawdown Schedule</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table stickyHeader size="small" sx={{ width: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Milestone</TableCell>
                      <TableCell>Draw Amount ($)</TableCell>
                      <TableCell>Cumulative Drawn ($)</TableCell>
                      <TableCell>Monthly Interest ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.milestoneTable.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{row.drawAmount}</TableCell>
                        <TableCell>{row.cumulativeDrawn}</TableCell>
                        <TableCell>{row.monthlyInterest}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </StyledColumn>
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default ConstructionLoan;
